// no React imports needed here

export function getEnv(name: string): string {
  try {
    const rt = (globalThis as unknown as { __ENV__?: Record<string, string | undefined> }).__ENV__;
    if (rt && typeof rt[name] === 'string') return (rt[name] as string).trim();
    const im = (globalThis as unknown as { importMetaEnv?: Record<string, string | undefined> })
      .importMetaEnv;
    const node = (
      globalThis as unknown as { process?: { env?: Record<string, string | undefined> } }
    ).process?.env;
    return (im?.[name] ?? node?.[name] ?? '').trim();
  } catch {
    return '';
  }
}

export function normalizeBase(input: string): string {
  if (!input) return '';
  let b = input.trim();
  b = b.replace(/\/?api\/docs\/?$/i, '');
  b = b.replace(/\/?docs\/?$/i, '');
  b = b.replace(/\/?api\/?$/i, '');
  return b.replace(/\/$/, '');
}

/**
 * Returns a display-friendly label for a base URL.
 * - Empty or "/" means same-origin (CloudFront proxy).
 * - Returns the normalized URL otherwise.
 */
export function displayBase(rawEnv: string | undefined): string {
  const raw = (rawEnv ?? '').trim();
  if (!raw || raw === '/') return '(same-origin)';
  return normalizeBase(raw) || '(same-origin)';
}

export function decodeBase64Url(input: string): string {
  const normalized = input
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(input.length + ((4 - (input.length % 4)) % 4), '=');
  try {
    if (typeof atob !== 'undefined') return atob(normalized);
  } catch {
    /* ignore */
  }
  try {
    return Buffer.from(normalized, 'base64').toString('utf-8');
  } catch {
    return '';
  }
}

export function decodeJwtClaims(token?: string | null): Record<string, unknown> | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const json = decodeBase64Url(parts[1]);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function redact(token?: string | null): string {
  if (!token) return '—';
  const len = token.length;
  if (len <= 12) return '*'.repeat(len);
  return `${token.slice(0, 6)}…${token.slice(-6)}`;
}

export function readStorage(key: string): {
  where: 'localStorage' | 'sessionStorage' | 'none';
  value: string | null;
} {
  try {
    const ls = localStorage.getItem(key);
    if (ls) return { where: 'localStorage', value: ls };
  } catch {
    /* noop */
  }
  try {
    const ss = sessionStorage.getItem(key);
    if (ss) return { where: 'sessionStorage', value: ss };
  } catch {
    /* noop */
  }
  return { where: 'none', value: null };
}

// tryFetch and statusTag moved to @digiwedge/ui-health-panels (ProbesPanel)

/**
 * Read a cookie value by name from document.cookie.
 */
export function readCookie(name: string): string | null {
  try {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [key, value] = cookie.split('=');
      if (key?.trim() === name) {
        return decodeURIComponent(value?.trim() ?? '');
      }
    }
  } catch {
    /* ignore */
  }
  return null;
}
